import styled from 'styled-components';
import { Button, Paper } from '@mui/material';

/**
 * The pre-order pages. These are public, so unlike the shop they do not sit
 * inside the patient sidebar and carry their own page width.
 *
 * Every block collapses at 900px and again at 600px, because almost all of
 * this traffic arrives from a phone.
 */

export const Page = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 32px 24px 64px;
  font-family: 'Poppins', sans-serif;
  color: #2B2B2B;

  /* The reserve bar floats over the foot of the window; without this the
     last section of the page sits underneath it. */
  &[data-sticky='true'] {
    padding-bottom: 124px;
  }

  @media (max-width: 600px) {
    padding: 20px 16px 48px;

    &[data-sticky='true'] {
      padding-bottom: 108px;
    }
  }
`;

/**
 * The progress bar across the top of the pre-order flow.
 *
 * Shown only to someone who has steps left to walk: a patient who is already
 * signed in reserves in one action, and a three-step bar in front of a
 * one-step job is a promise of work that is not there.
 */
export const Steps = styled.ol`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  list-style: none;
  margin: 0 0 28px;
  padding: 0;
  counter-reset: step;

  @media (max-width: 600px) {
    gap: 4px;
    margin-bottom: 20px;
  }
`;

export const Step = styled.li`
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  position: relative;
  font-family: 'Poppins', sans-serif;

  /* The connector sits behind the markers and stops at the first step. */
  &::before {
    content: '';
    position: absolute;
    top: 15px;
    right: 50%;
    width: 100%;
    height: 2px;
    background: ${(p) => (p['data-state'] === 'upcoming' ? '#E4E4ED' : '#1AA19C')};
  }

  &:first-child::before { display: none; }

  .marker {
    position: relative;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    margin: 0 auto 8px;
    border-radius: 50%;
    font-size: 13px;
    font-weight: 600;
    background: ${(p) => {
    if (p['data-state'] === 'done') return '#1AA19C';
    if (p['data-state'] === 'current') return '#FFFFFF';
    return '#F2F2F7';
  }};
    color: ${(p) => {
    if (p['data-state'] === 'done') return '#FFFFFF';
    if (p['data-state'] === 'current') return '#1AA19C';
    return '#9A9AAB';
  }};
    border: 2px solid ${(p) => (p['data-state'] === 'upcoming' ? '#E4E4ED' : '#1AA19C')};
  }

  .label {
    font-size: 13px;
    line-height: 1.4;
    color: ${(p) => (p['data-state'] === 'upcoming' ? '#9A9AAB' : '#2B2B2B')};
    font-weight: ${(p) => (p['data-state'] === 'current' ? 600 : 400)};
  }

  @media (max-width: 600px) {
    .marker { width: 28px; height: 28px; margin-bottom: 6px; }
    &::before { top: 13px; }
    .label { font-size: 11px; }
  }
`;

/**
 * The heading band across the top of the public pre-order pages.
 *
 * The site header sits above it with the logo and the main nav, so this
 * repeats no branding: it names the flow the visitor is in and carries the one
 * promise that holds on every screen of it. What was here before was a 13px
 * grey eyebrow, which is easy to miss on the page someone lands on cold from
 * an ad.
 */
export const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  padding: 16px 20px;
  border-radius: 14px;
  border: 1px solid #E1EDED;
  background: linear-gradient(90deg, #F3FAFA 0%, #FFFFFF 72%);

  .lead {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  .badge {
    display: grid;
    place-items: center;
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: #FFFFFF;
    border: 1px solid #DCEDEC;
    color: #1AA19C;
  }

  .title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    color: #2B2B2B;
  }

  .subtitle {
    margin-top: 2px;
    font-size: 13px;
    line-height: 1.5;
    color: #7A7A8C;
  }

  @media (max-width: 600px) {
    padding: 14px 16px;
    margin-bottom: 18px;
    gap: 12px;

    .badge { flex-basis: 36px; width: 36px; height: 36px; border-radius: 10px; }
    .title { font-size: 17px; }
    .subtitle { font-size: 12px; }

    /* Wrapped onto its own line, the action reads as a row rather than a
       button stranded under the text. */
    > button { width: 100%; }
  }
`;

/**
 * The product picker above the hero. Shown only when there is more than one
 * open campaign, so a single-product launch looks exactly as it did before.
 */
export const ProductPicker = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 8px;
    margin-bottom: 20px;
  }
`;

export const ProductTab = styled.button`
  flex: 1 1 220px;
  text-align: left;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  border-radius: 12px;
  padding: 14px 16px;
  background: ${(p) => (p['data-selected'] === 'true' ? '#F4FBFB' : '#FFFFFF')};
  border: 1.5px solid ${(p) => (p['data-selected'] === 'true' ? '#1AA19C' : '#E4E4ED')};
  transition: border-color .15s ease, background .15s ease;

  &:hover { border-color: #1AA19C; }

  /*
   * The package is the option we want read first, so it carries a badge and,
   * while it is not the one selected, a tinted border that lifts it out of the
   * row. Written as its own attribute rule rather than folded into the border
   * above so that the selected state still wins: an attribute selector is
   * more specific than the plain declaration, so the two are kept apart by
   * also matching on data-selected.
   */
  &[data-recommended='true'][data-selected='false'] {
    border-color: #A8DEDB;
    background: #FCFEFE;
  }

  .recommended {
    display: inline-block;
    margin-bottom: 8px;
    padding: 3px 10px;
    border-radius: 999px;
    background: #1AA19C;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: .5px;
    text-transform: uppercase;
    color: #FFFFFF;
  }

  .name {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: #2B2B2B;
  }

  .price {
    display: block;
    margin-top: 2px;
    font-size: 13px;
    color: #7A7A8C;
  }

  .sold-out {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    font-weight: 600;
    color: #B33A3A;
  }

  /* Only a package carries one: it is the difference against its own parts. */
  .save {
    display: inline-block;
    margin-top: 6px;
    padding: 2px 8px;
    border-radius: 999px;
    background: #E6F6F1;
    font-size: 11.5px;
    font-weight: 700;
    color: #15795A;
  }

  /*
   * On a phone the tab becomes two columns instead of four stacked lines.
   *
   * Stacked, the package ran to badge / name / price / saving - about 90px
   * - and the three tabs together pushed the product itself off the first
   * screen. Here the name and the price share the top line, and the badges
   * share the line under it, so a single device needs one line and the
   * package two.
   *
   * Grid rather than flex-wrap: where the items break has to be decided,
   * not left to whether that particular product name happens to fit. The
   * order properties reorder the placement without touching the markup, so
   * the button's children, its role and its handler are all untouched.
   *
   * Every child is placed in a named column, including the two that appear
   * only sometimes, so a sold-out package cannot land on top of its badge.
   */
  @media (max-width: 600px) {
    flex: 1 1 100%;
    padding: 12px 14px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: baseline;
    column-gap: 10px;
    row-gap: 6px;

    .name {
      order: 1;
      grid-column: 1;
      font-size: 14.5px;
    }

    /* Reads as the price now that it sits opposite the name, rather than as
       a caption under it. */
    .price {
      order: 2;
      grid-column: 2;
      justify-self: end;
      margin-top: 0;
      font-size: 13.5px;
      font-weight: 600;
      color: #2B2B2B;
    }

    .recommended {
      order: 3;
      grid-column: 1;
      justify-self: start;
      margin-bottom: 0;
    }

    .save {
      order: 4;
      grid-column: 2;
      justify-self: end;
      margin-top: 0;
    }

    .sold-out {
      order: 5;
      grid-column: 1;
      justify-self: start;
      margin-top: 0;
    }
  }
`;

export const Hero = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

/**
 * The photograph fills its frame edge to edge.
 *
 * The frame is constrained by WIDTH, not height: the column is wider than the
 * tallest comfortable 4:5 photo, so capping the height instead left the image
 * floating in ~100px of unused column. Worse, the panel tone (#F7F8FA) is all
 * but identical to the page background, so that gap read as dead space rather
 * than as a panel.
 *
 * Height follows the photo's own proportions, so every shot is shown whole -
 * never cropped, never stretched - and the frame is the same size for each.
 */
export const Gallery = styled.div`
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  background: #F7F8FA;
  border: 1px solid #ECECF2;
  border-radius: 14px;
  overflow: hidden;
  line-height: 0;

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  /*
   * No photograph yet.
   *
   * A 4:5 frame here left a ~550px void in almost exactly the page's own
   * colour with a small grey icon adrift in the middle of it, which read as a
   * page that had failed to load rather than as a product whose pictures are
   * still being shot. The empty state is therefore its own shape - square
   * rather than tall, marked out with a dashed edge, and captioned - so that
   * it is plainly deliberate while still balancing the panel beside it.
   */
  &[data-empty='true'] {
    aspect-ratio: 1 / 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #FBFBFD;
    border-style: dashed;
    border-color: #DEDEE8;
    line-height: 1.5;

    .caption {
      padding: 0 24px;
      font-size: 13px;
      font-weight: 500;
      color: #9A9AAB;
      text-align: center;
    }
  }
`;

export const GalleryThumbs = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
  /* Lined up with the frame above rather than the wider column. */
  max-width: 440px;
  margin-left: auto;
  margin-right: auto;
`;

export const GalleryThumb = styled.button`
  width: 72px;
  aspect-ratio: 4 / 5;
  /* Keeps its shape in the rail. The rail is a flex column with a height
     limit, and a flex item shrinks to fit one by default, which squashed
     every thumbnail out of proportion instead of letting the rail scroll. */
  flex: 0 0 auto;
  padding: 0;
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
  background: #F7F8FA;
  border: 1.5px solid ${(p) => (p['data-active'] === 'true' ? '#1AA19C' : '#E4E4ED')};
  transition: border-color .15s ease;

  &:hover { border-color: #1AA19C; }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: 600px) {
    width: 60px;
  }
`;

export const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px;
  line-height: 1.25;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

export const Price = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: #2B2B2B;
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    font-size: 24px;
  }
`;

export const Muted = styled.div`
  font-size: 13px;
  color: #7A7A8C;
  line-height: 1.6;
`;

export const Highlights = styled.ul`
  margin: 20px 0 0;
  padding-left: 20px;

  li {
    font-size: 14px;
    color: #4A4A5A;
    margin-bottom: 8px;
    line-height: 1.6;
  }
`;

export const DispatchBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #EAF7F1;
  color: #15795A;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  margin: 16px 0 4px;
`;

export const PlanList = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 12px;
`;

/* The whole card is the control, so the tap target is the card on a phone. */
export const PlanCard = styled.div`
  border: 1.5px solid ${(p) => (p['data-selected'] === 'true' ? '#1AA19C' : '#E4E4ED')};
  background: ${(p) => (p['data-selected'] === 'true' ? '#F4FBFB' : '#FFFFFF')};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: border-color .15s ease, background .15s ease;

  &:hover {
    border-color: #1AA19C;
  }
`;

export const PlanTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const PlanTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
`;

export const PlanAmount = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1AA19C;
  white-space: nowrap;
`;

export const SaveTag = styled.span`
  background: #EAF7F1;
  color: #15795A;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
`;

export const PrimaryButton = styled(Button)`
  && {
    font-family: 'Poppins', sans-serif;
    text-transform: none;
    font-weight: 500;
    font-size: 15px;
    color: #FFFFFF;
    background: linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%);
    border-radius: 8px;
    padding: 11px 26px;
    box-shadow: none;

    &:hover:not(.Mui-disabled) { opacity: .92; box-shadow: none; }

    /*
     * Flat grey rather than a faded gradient. A gradient at reduced opacity
     * still reads as the primary action, just a bit washed out.
     */
    &.Mui-disabled {
      background: #E8EAEF;
      color: #A7AEBA;
      box-shadow: none;
      cursor: not-allowed;
    }

    @media (max-width: 600px) {
      width: 100%;
    }
  }
`;

export const OutlineButton = styled(Button)`
  && {
    font-family: 'Poppins', sans-serif;
    text-transform: none;
    font-weight: 500;
    font-size: 14px;
    color: #097EB7;
    border: 1px solid #CFD8E3;
    border-radius: 8px;
    padding: 9px 20px;
    background: #FFFFFF;

    /* Not on a disabled button: a hover response reads as "this works". */
    &:hover:not(.Mui-disabled) { border-color: #097EB7; background: #F7FBFF; }

    /*
     * The double-ampersand wrapper above beats MUI's own disabled styling, so
     * without this a disabled OutlineButton keeps its full brand colour and
     * looks pressable. (No backticks in here: this is inside a template
     * literal, and one would end the string.)
     */
    &.Mui-disabled {
      color: #A7AEBA;
      border-color: #E8EAEF;
      background: #F7F8FA;
      cursor: not-allowed;
    }
  }
`;

export const Card = styled(Paper)`
  && {
    border: 1px solid #ECECF2;
    border-radius: 14px;
    padding: 24px;
    box-shadow: none;

    @media (max-width: 600px) {
      padding: 16px;
    }
  }
`;

export const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 14px;
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  .full { grid-column: 1 / -1; }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    .full { grid-column: auto; }
  }
`;

export const ConsentRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #F2F2F7;
  cursor: pointer;

  &:last-child { border-bottom: 0; }

  /*
   * A checkbox renders at 13px by default, which is a hard thing to hit on
   * a phone - and this one gates the reserve button, so a missed tap is a
   * lost order. The row is a <label>, so its text toggles the box too and
   * the real target is the full width of the row; this just makes the box
   * itself worth aiming at.
   */
  input {
    margin-top: 1px;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    accent-color: #1AA19C;
  }

  span { font-size: 14px; line-height: 1.6; color: #4A4A5A; }
`;

export const ConsentTag = styled.span`
  && {
    font-size: 11px;
    font-weight: 600;
    border-radius: 5px;
    padding: 1px 6px;
    margin-left: 6px;
    background: ${(p) => (p['data-required'] === 'true' ? '#FDECEC' : '#F1F1F6')};
    color: ${(p) => (p['data-required'] === 'true' ? '#C0392B' : '#7A7A8C')};
  }
`;

export const SummaryLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  font-size: 14px;
  color: #4A4A5A;

  &[data-strong='true'] {
    font-weight: 600;
    color: #2B2B2B;
    border-top: 1px solid #ECECF2;
    margin-top: 6px;
    padding-top: 12px;
    font-size: 15px;
  }
`;

export const Notice = styled.div`
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  margin: 16px 0;
  background: ${(p) => (p['data-tone'] === 'error' ? '#FDECEC' : '#F4F8FD')};
  color: ${(p) => (p['data-tone'] === 'error' ? '#B33A3A' : '#3C5A7A')};
`;

export const StatusPill = styled.span`
  display: inline-block;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  background: #F1F1F6;
  color: #5A5A6E;

  &[data-tone='good'] { background: #EAF7F1; color: #15795A; }
  &[data-tone='warn'] { background: #FFF6E5; color: #96670A; }
  &[data-tone='bad'] { background: #FDECEC; color: #B33A3A; }
`;

export const OrderGrid = styled.div`
  display: grid;
  gap: 16px;
`;

/**
 * One pre-order in the list. The whole card is clickable, with the facts in a
 * definition list so each value is labelled rather than run together in a
 * sentence - a patient scanning three orders reads the labels, not prose.
 */
export const OrderCard = styled(Paper)`
  && {
    border: 1px solid #ECECF2;
    border-radius: 14px;
    padding: 20px 22px;
    box-shadow: none;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 20px;
    align-items: center;
    cursor: pointer;
    transition: border-color .15s ease, box-shadow .15s ease;

    &:hover {
      border-color: #CFE3EF;
      box-shadow: 0 2px 10px rgba(16, 42, 67, .06);
    }

    @media (max-width: 760px) {
      grid-template-columns: 1fr;
      padding: 16px;
      gap: 14px;
    }
  }
`;

export const OrderCardBody = styled.div`
  min-width: 0;

  .title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 14px;
  }

  .title {
    font-size: 16px;
    font-weight: 600;
    color: #2B2B2B;
  }

  /* Label above value, wrapping naturally as the card narrows. */
  .facts {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 32px;
    margin: 0;
  }

  .facts div { min-width: 0; }

  .facts dt {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .5px;
    text-transform: uppercase;
    color: #9A9AAB;
    margin-bottom: 2px;
  }

  .facts dd {
    margin: 0;
    font-size: 14px;
    color: #4A4A5A;
  }

  .note {
    margin-top: 14px;
    font-size: 13px;
    color: #6B6B7B;
  }

  .note-due {
    color: #96670A;
    font-weight: 600;
  }
`;

export const OrderCardSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  white-space: nowrap;

  .amount {
    font-size: 20px;
    font-weight: 600;
    color: #2B2B2B;
  }

  @media (max-width: 760px) {
    align-items: stretch;
    border-top: 1px solid #F2F2F7;
    padding-top: 14px;

    .amount { font-size: 18px; }
  }
`;

/* Shown when there is nothing to list, instead of a bare line of grey text. */
export const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  border: 1px dashed #E4E4ED;
  border-radius: 14px;
  margin-top: 24px;

  h3 {
    margin: 14px 0 6px;
    font-size: 16px;
    font-weight: 600;
    color: #2B2B2B;
  }
`;

export const OrderRow = styled(Paper)`
  && {
    border: 1px solid #ECECF2;
    border-radius: 12px;
    padding: 18px;
    box-shadow: none;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 14px;
    align-items: center;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
      padding: 14px;
    }
  }
`;

export const ScheduleTable = styled.div`
  margin-top: 8px;

  > div {
    display: grid;
    grid-template-columns: 1.2fr 1fr 1fr auto;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #F2F2F7;
    font-size: 14px;
    align-items: center;
  }

  > div:last-child { border-bottom: 0; }

  .head {
    font-size: 12px;
    font-weight: 600;
    color: #7A7A8C;
    text-transform: uppercase;
    letter-spacing: .4px;
  }

  @media (max-width: 600px) {
    > div {
      grid-template-columns: 1fr 1fr;
      row-gap: 6px;
    }
    .head { display: none; }
  }
`;

export const CenterState = styled.div`
  text-align: center;
  padding: 64px 16px;
  color: #7A7A8C;
  font-size: 15px;

  /*
   * The waiting state, used both for the first load and for switching from
   * one device to another.
   *
   * The minimum height is the point of it. Switching product replaces the
   * whole page below the picker, and without a floor the document collapses
   * from several thousand pixels to a strip and is thrown open again a
   * moment later - which shunts the picker up the screen under the reader's
   * thumb just as they are looking at it.
   */
  &[data-busy='true'] {
    display: grid;
    justify-items: center;
    /*
     * align-content, not align-items: the rows have to pack together and be
     * centred as a group. Centring each row inside its own share of the
     * grid instead left the spinner and its label about 400px apart, which
     * read as two unrelated things on an empty page.
     */
    align-content: center;
    gap: 14px;
    min-height: 52vh;
    padding: 32px 16px;
  }
`;

/* ------------------------------------------------------------------ *
 * The public product page.
 *
 * Everything below dresses the landing page: the buy panel beside the
 * photographs, the bar that follows the visitor down the page, and the
 * content sections under it. None of it carries state or logic - the page
 * owns all of that - so these are safe to restyle without touching the
 * reserve flow.
 * ------------------------------------------------------------------ */

/** Thumbnail rail beside the photograph on desktop, a row under it on mobile. */
export const GalleryPane = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p['data-rail'] === 'true' ? '90px minmax(0, 1fr)' : 'minmax(0, 1fr)')};
  gap: 16px;
  align-items: start;

  /*
   * The photographs hold still while the panel beside them scrolls.
   *
   * The buy panel is much the taller of the two columns - price, plan,
   * quantity, four agreements, the button and the trust row - so reading
   * down it used to carry the product itself off the top of the screen,
   * which is the one thing a visitor wants in view while they decide.
   *
   * Only where there are two columns to hold still between. Below 900px the
   * grid is a single column and the panel sits under the photographs, so
   * pinning them would park a picture over the thing being read.
   *
   * Offset by the height of the site header, which is itself sticky at the
   * top of the page, plus the page's own gutter.
   */
  @media (min-width: 901px) {
    position: sticky;
    top: 101px;
    /* Sticky needs a box shorter than its parent's to travel in; the Hero
       grid already starts its items rather than stretching them, and this
       says so where the sticky is, so a later change to Hero cannot quietly
       freeze this in place. */
    align-self: start;
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ThumbRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  /*
   * The rail is pinned beside the photograph, so it has to fit the screen:
   * a campaign with nine shots makes a rail taller than the window, and
   * anything past the fold would sit there out of reach, since the pane it
   * is in no longer scrolls with the page.
   *
   * The height left for it is the window less the offset it is pinned at
   * and the reserve bar across the foot. The gutter is reserved whether or
   * not it ends up scrolling, so a campaign with four photographs lines up
   * with one that has nine.
   */
  @media (min-width: 901px) {
    max-height: calc(100vh - 190px);
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-gutter: stable;
  }

  @media (max-width: 900px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    order: 2;
    margin-top: 12px;
  }
`;

/** The panel that holds everything a visitor needs in order to reserve. */
export const BuyBox = styled.div`
  background: #FFFFFF;
  border: 1px solid #ECECF2;
  border-radius: 16px;
  padding: 26px;

  @media (max-width: 600px) {
    padding: 18px;
    border-radius: 14px;
  }
`;

export const Tagline = styled.p`
  margin: 10px 0 18px;
  font-size: 14px;
  line-height: 1.7;
  color: #5A5A6B;
`;

/** The price it would be without the pre-order, when the API sends one. */
export const Mrp = styled.span`
  font-size: 17px;
  font-weight: 400;
  color: #9A9AAB;
  text-decoration: line-through;
`;

export const StockPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p['data-tone'] === 'bad' ? '#B33A3A' : '#15795A')};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/** Shipping, warranty and payment assurances, as a row of small claims. */
export const TrustRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #F2F2F7;

  span {
    display: block;
    font-size: 11.5px;
    line-height: 1.45;
    color: #7A7A8C;
    text-align: center;
  }

  svg {
    display: block;
    margin: 0 auto 6px;
    color: #1AA19C;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }
`;

export const QtyBox = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #E4E4ED;
  border-radius: 10px;
  overflow: hidden;

  button {
    width: 38px;
    height: 38px;
    border: 0;
    background: #FFFFFF;
    color: #2B2B2B;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;

    &:hover:not(:disabled) { background: #F4FBFB; color: #1AA19C; }
    &:disabled { color: #C9C9D4; cursor: not-allowed; }
  }

  .count {
    min-width: 42px;
    text-align: center;
    font-size: 15px;
    font-weight: 600;
    border-left: 1px solid #E4E4ED;
    border-right: 1px solid #E4E4ED;
    padding: 9px 0;
  }

  /*
   * 38px is comfortable under a mouse and small under a thumb - and this is
   * the control standing between someone and a second unit. 44px is the
   * figure every touch guideline lands on, so the stepper grows to it where
   * the pointer is likely to be a finger.
   */
  @media (max-width: 600px) {
    button {
      width: 44px;
      height: 44px;
      font-size: 20px;
    }

    .count {
      min-width: 46px;
      padding: 11px 0;
    }
  }
`;

/**
 * The reserve action, repeated at the bottom of the window.
 *
 * The buy panel scrolls away within one screen on a phone, and everything
 * below it is reasons to buy with no way to act on them. This is the same
 * action and the same disabled rules, not a second path.
 */
export const StickyBuyBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, .97);
  border-top: 1px solid #ECECF2;
  box-shadow: 0 -6px 20px rgba(16, 24, 40, .06);

  .what {
    min-width: 0;
  }

  .name {
    font-size: 14px;
    font-weight: 600;
    color: #2B2B2B;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cost {
    font-size: 12.5px;
    color: #7A7A8C;
    margin-top: 2px;
  }

  @media (max-width: 600px) {
    padding: 10px 14px;
    gap: 10px;

    .name { font-size: 13px; }
    /*
     * The button is the point of the bar, but not the whole of it: the
     * double ampersand out-specifies PrimaryButton's own full-width rule,
     * which is right inside the panel and wrong beside a label.
     */
    && > button { width: auto; flex: 0 0 auto; padding: 10px 18px; }
  }
`;

export const SectionHead = styled.div`
  text-align: center;
  margin: 64px auto 28px;
  max-width: 640px;

  h2 {
    font-size: 25px;
    font-weight: 600;
    color: #2B2B2B;
    margin: 0;
    line-height: 1.3;
  }

  p {
    margin: 10px 0 0;
    font-size: 14px;
    line-height: 1.7;
    color: #7A7A8C;
  }

  @media (max-width: 600px) {
    margin: 44px auto 22px;
    h2 { font-size: 21px; }
  }
`;

export const ValueStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ValueCard = styled.div`
  background: #FAFAFC;
  border: 1px solid #EFEFF4;
  border-radius: 14px;
  padding: 20px;

  svg { color: #1AA19C; }

  .title {
    margin-top: 10px;
    font-size: 15px;
    font-weight: 600;
    color: #2B2B2B;
  }

  .note {
    margin-top: 6px;
    font-size: 13px;
    line-height: 1.65;
    color: #7A7A8C;
  }
`;

/**
 * A feature: picture on one side, the claim on the other, alternating down
 * the page. data-flip swaps the sides; a feature with no picture becomes a
 * plain full-width panel rather than half an empty row.
 */
export const FeatureRow = styled.section`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 40px;
  align-items: center;
  background: #FAFAFC;
  border: 1px solid #EFEFF4;
  border-radius: 18px;
  padding: 28px;
  margin-bottom: 20px;

  .shot {
    border-radius: 14px;
    overflow: hidden;
    background: #FFFFFF;
    border: 1px solid #ECECF2;
    line-height: 0;
    order: ${(p) => (p['data-flip'] === 'true' ? 2 : 1)};
  }

  .shot img {
    width: 100%;
    height: auto;
    display: block;
  }

  .copy {
    order: ${(p) => (p['data-flip'] === 'true' ? 1 : 2)};
  }

  h3 {
    margin: 0 0 12px;
    font-size: 21px;
    font-weight: 600;
    line-height: 1.35;
    color: #2B2B2B;
  }

  p {
    margin: 0;
    font-size: 14.5px;
    line-height: 1.8;
    color: #5A5A6B;
  }

  &[data-solo='true'] {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 22px;
    padding: 22px;

    /* Picture first on a narrow screen, whichever side it took above. */
    .shot { order: 1; }
    .copy { order: 2; }
    h3 { font-size: 18px; }
  }
`;

/**
 * The ambient product loop that heads the band.
 *
 * A few silent seconds of the device moving, which is the one thing a still
 * photograph cannot do. It is framed exactly like the panels below it so the
 * two read as one section rather than a video bolted onto a grid.
 *
 * The pause button is not optional decoration. A loop runs indefinitely, and
 * anything that moves for more than five seconds without a way to stop it is
 * a WCAG 2.2.2 failure - and genuinely unpleasant for anyone who finds motion
 * distracting. Someone who has asked their system for reduced motion gets it
 * paused on the poster frame instead, and starts it themselves if they want.
 *
 * White rather than black while it is empty: the frame has no content for a
 * moment before the first frame decodes, and a black rectangle on a white
 * page reads as something that failed to load.
 */
export const VideoFrame = styled.div`
  position: relative;
  /* Flush with the grid of panels below it, so the two read as one block
     rather than a narrower video floating above a wider grid. */
  margin: 0 0 20px;
  border: 1px solid #ECECF2;
  border-radius: 18px;
  overflow: hidden;
  background: #FFFFFF;
  line-height: 0;

  video {
    display: block;
    width: 100%;
    height: auto;
    /*
     * The footage does not exist yet, so neither of these assumes its shape.
     * The cap stops a square or portrait clip from filling the window at
     * this width, and contain letterboxes rather than crops - a crop
     * chosen here, sight unseen, would cut the device out of its own frame.
     */
    max-height: 640px;
    object-fit: contain;
  }

  button {
    position: absolute;
    right: 14px;
    bottom: 14px;
    width: 38px;
    height: 38px;
    display: grid;
    place-items: center;
    padding: 0;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    color: #FFFFFF;
    /* Over footage we have not seen yet, so it carries its own contrast
       rather than trusting the frame underneath it to be dark enough. */
    background: rgba(26, 26, 38, 0.55);
    transition: background 0.15s ease;
  }

  button:hover {
    background: rgba(26, 26, 38, 0.78);
  }

  button:focus-visible {
    outline: 2px solid #1AA19C;
    outline-offset: 2px;
  }

  @media (max-width: 600px) {
    border-radius: 14px;

    button {
      right: 10px;
      bottom: 10px;
    }
  }
`;

/**
 * The supplied marketing panels, in a band of their own.
 *
 * Each one is finished artwork: it carries its own heading, its own feature
 * list and a screenshot of the app, all set into a background that runs to
 * the edge. So this frames them and does nothing else - no caption, no
 * cropping, no text laid over them. A heading of ours beside the heading
 * printed on the panel reads as a stutter, which is why they are not fed
 * through FeatureRow with the rest of the page's pictures.
 *
 * Two across at most, whatever the width. The panels are portrait, and the
 * smallest type on them is the axis labels inside the phone screenshot;
 * below about a third of this page's width that stops being readable, and a
 * poster nobody can read is just a coloured rectangle.
 */
export const PosterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  align-items: start;

  figure {
    margin: 0;
    border: 1px solid #ECECF2;
    border-radius: 18px;
    overflow: hidden;
    background: #FFFFFF;
    /* The frame hugs the artwork: an inline image otherwise leaves a strip
       of descender space below itself inside the rounded corner. */
    line-height: 0;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

/** The classification line under the price: quiet, but never hidden. */
export const ProductClass = styled.div`
  margin-top: 6px;
  font-size: 12.5px;
  line-height: 1.6;
  color: #7A7A8C;
`;

/**
 * Back to the top of a very long page.
 *
 * Fixed above the reserve bar rather than beside it: the bar is the thing
 * the page is for, and on a phone the two would otherwise sit on the same
 * line and compete. Offset enough to clear it, and less when the bar is
 * not there.
 *
 * Shown only once there is something to scroll back from - a control that
 * is there before you have moved is noise.
 */
export const ToTopButton = styled.button`
  position: fixed;
  right: 24px;
  bottom: 96px;
  z-index: 1050;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 1px solid #E4E4ED;
  border-radius: 50%;
  cursor: pointer;
  color: #2B2B2B;
  background: rgba(255, 255, 255, .97);
  box-shadow: 0 4px 14px rgba(16, 24, 40, .12);
  transition: background .15s ease, border-color .15s ease;

  &:hover {
    border-color: #1AA19C;
    color: #1AA19C;
  }

  &:focus-visible {
    outline: 2px solid #1AA19C;
    outline-offset: 2px;
  }

  /* No reserve bar below it on a closed campaign, so it can sit lower. */
  &[data-bar='false'] {
    bottom: 24px;
  }

  @media (max-width: 600px) {
    right: 14px;
    bottom: 88px;

    &[data-bar='false'] {
      bottom: 16px;
    }
  }
`;

export const TagGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const TagItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px 18px;
  border: 1px solid #E1EDED;
  border-radius: 12px;
  background: linear-gradient(135deg, #F7FCFC 0%, #FFFFFF 70%);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: #2B2B2B;

  svg { flex: 0 0 auto; color: #1AA19C; margin-top: 1px; }
`;

/**
 * A question and its answer, open and closed by the browser.
 *
 * Built on the native disclosure element on purpose: it is keyboard
 * accessible and searchable in the page without this component holding any
 * state of its own.
 */
export const FaqItem = styled.details`
  border-bottom: 1px solid #ECECF2;

  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 2px;
    cursor: pointer;
    list-style: none;
    font-size: 15.5px;
    font-weight: 600;
    color: #2B2B2B;
  }

  summary::-webkit-details-marker { display: none; }

  /* The sign flips to a minus when the answer is open. */
  summary::after {
    content: '+';
    flex: 0 0 auto;
    font-size: 22px;
    font-weight: 400;
    line-height: 1;
    color: #1AA19C;
  }

  &[open] summary::after { content: '\\2212'; }

  .answer {
    padding: 0 2px 20px;
    font-size: 14.5px;
    line-height: 1.8;
    color: #5A5A6B;
  }

  @media (max-width: 600px) {
    summary { font-size: 14.5px; padding: 15px 2px; }
    .answer { font-size: 13.5px; }
  }
`;

/* ------------------------------------------------------------------
 * Packages
 *
 * A package is an ordinary campaign that happens to ship two devices, so it
 * reuses the whole product page. These are only the parts that have to say
 * something a single device never needs to: what is inside it, what the
 * parts cost separately, and - on a device's own page - that a package
 * containing it exists.
 * ------------------------------------------------------------------ */

/** The devices inside a package, side by side. */
export const PackageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const PackageCard = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #ECECF2;
  border-radius: 14px;
  padding: 16px;

  .shot {
    flex: 0 0 76px;
    width: 76px;
    height: 76px;
    border-radius: 10px;
    overflow: hidden;
    background: #F7F8FA;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .shot img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .shot svg { color: #CFCFD7; }

  .name {
    font-size: 15px;
    font-weight: 600;
    color: #2B2B2B;
  }

  .alone {
    margin-top: 4px;
    font-size: 13px;
    color: #7A7A8C;
  }

  a {
    display: inline-block;
    margin-top: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1AA19C;
    text-decoration: none;
    cursor: pointer;
  }

  a:hover { text-decoration: underline; }
`;

/**
 * The arithmetic, stated plainly under the contents: what the two devices
 * cost on their own, and what the package takes off. Both figures come from
 * the component campaigns, so neither is a marketing number.
 */
export const PackageSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 14px;
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 14px;
  background: #F4FBFB;
  border: 1px solid #D7EFEE;

  .label { font-size: 13.5px; color: #5A5A6B; }

  .struck {
    font-size: 15px;
    color: #9A9AAB;
    text-decoration: line-through;
  }

  .saving {
    font-size: 15px;
    font-weight: 700;
    color: #15795A;
  }
`;

/**
 * On a single device's page: a quiet line saying the device is also sold in
 * a package, with what that saves. It is an offer, not an interruption - no
 * modal, nothing that has to be dismissed before reserving the device the
 * visitor actually came for.
 */
export const CrossSell = styled.aside`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 28px;
  padding: 18px 22px;
  border-radius: 16px;
  background: #F4FBFB;
  border: 1px solid #D7EFEE;

  .title {
    font-size: 15px;
    font-weight: 600;
    color: #2B2B2B;
  }

  .note {
    margin-top: 4px;
    font-size: 13.5px;
    line-height: 1.6;
    color: #5A5A6B;
  }

  && > button {
    width: auto;
    flex: 0 0 auto;
    padding: 10px 20px;
  }

  @media (max-width: 600px) {
    padding: 16px;

    && > button { width: 100%; }
  }
`;
